import { ROUTERS } from '@/constant';
import { useDeviceCheck } from '@/hooks';
import {
	getMobileRouteFromScreen,
	getTabletRouteFromScreen,
} from '@/utils/device-route.util';
import { lazy, memo, useEffect } from 'react';
import {
	Navigate,
	Route,
	Routes,
	useLocation,
	useNavigate,
} from 'react-router-dom';

const LoginPage = lazy(() => import('@/pages/login'));
const LoginMobilePage = lazy(() => import('@/pages/login/login.view.mobile'));
const Tab01Page = lazy(() => import('@/pages/tablet/tab-01'));
const Tab02Page = lazy(() => import('@/pages/tablet/tab-02'));
const Mob01Page = lazy(() => import('@/pages/mobile/mob-01'));
const Mob02Page = lazy(() => import('@/pages/mobile/mob-02'));
const Mob03Page = lazy(() => import('@/pages/mobile/mob-03'));
const OAuthCallback = lazy(() => import('@/pages/oauth'));
const VerifyPage = lazy(() => import('@/pages/verify'));

/**
 * Component to handle device-based routing logic
 * Uses useDeviceCheck hook to call real API and determine which screen to show
 */
const DeviceRouteHandler = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const {
		deviceStatus,
		screen,
		deviceType: detectedDeviceType,
		isLoading,
		isChecking,
		error,
		rejectReason,
	} = useDeviceCheck();

	useEffect(() => {
		// Skip if on specific routes (direct access)
		const publicRoutes = [
			ROUTERS.OAUTH_CALLBACK,
			ROUTERS.TAB_01,
			ROUTERS.TAB_02,
			ROUTERS.TAB_03,
			ROUTERS.MOB_01,
			ROUTERS.MOB_02,
			ROUTERS.MOB_03,
			ROUTERS.MOB_04,
			ROUTERS.VERIFY,
		];

		if (publicRoutes.includes(location.pathname)) {
			return;
		}

		// PC keeps the old flow - no device check needed
		if (detectedDeviceType === 'pc') {
			return;
		}

		// Wait for loading to complete
		if (isLoading || isChecking) {
			return;
		}

		// If there's an error or no screen info, redirect to registration
		if (error || !screen) {
			if (detectedDeviceType === 'mobile') {
				navigate(ROUTERS.MOB_01, { replace: true });
			} else if (detectedDeviceType === 'tablet') {
				navigate(ROUTERS.TAB_01, { replace: true });
			}
			return;
		}

		// Navigate based on screen response from API
		const targetRoute =
			detectedDeviceType === 'mobile'
				? getMobileRouteFromScreen(screen)
				: detectedDeviceType === 'tablet'
					? getTabletRouteFromScreen(screen)
					: '/';

		// Pass rejection reason as state if rejected
		if (screen === 'REJECTED_NOTICE' && rejectReason) {
			navigate(targetRoute, {
				replace: true,
				state: { rejectReason, deviceStatus },
			});
		} else {
			navigate(targetRoute, {
				replace: true,
				state: { deviceStatus },
			});
		}
	}, [
		detectedDeviceType,
		screen,
		deviceStatus,
		isLoading,
		isChecking,
		error,
		rejectReason,
		navigate,
		location.pathname,
	]);

	// Mobile / tablet never fall through to <LoginPage /> — that's the PC
	// login and would flash the mobile-login view for one tick before the
	// effect above navigates to /mob-01 or /tab-01. Show the loader until the
	// navigation lands.
	if (detectedDeviceType !== 'pc') {
		return (
			<div className="min-h-screen flex items-center justify-center bg-[#F5F5F5]">
				<div className="text-center">
					<div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4" />
					<p className="text-font2 text-[14px]">
						기기 정보를 확인하고 있습니다...
					</p>
				</div>
			</div>
		);
	}

	// PC shows login page
	return <LoginPage />;
};

const AuthRouter = () => {
	return (
		<Routes>
			<Route path={ROUTERS.OAUTH_CALLBACK} element={<OAuthCallback />} />
			{/* Tablet routes */}
			<Route path={ROUTERS.TAB_01} element={<Tab01Page />} />
			<Route path={ROUTERS.TAB_02} element={<Tab02Page />} />
			<Route path={ROUTERS.TAB_03} element={<LoginMobilePage />} />
			{/* Mobile routes */}
			<Route path={ROUTERS.MOB_01} element={<Mob01Page />} />
			<Route path={ROUTERS.MOB_02} element={<Mob02Page />} />
			<Route path={ROUTERS.MOB_03} element={<Mob03Page />} />
			<Route path={ROUTERS.MOB_04} element={<LoginMobilePage />} />
			{/* OTP verification route */}
			<Route path={ROUTERS.VERIFY} element={<VerifyPage />} />
			{/* Default route with device detection */}
			<Route index element={<DeviceRouteHandler />} />
			{/* Catch-all route - redirect to index (login page) */}
			<Route path="*" element={<Navigate to="/" replace />} />
		</Routes>
	);
};

export default memo(AuthRouter);
